import { useEffect } from 'react';

/**
 * Optimized keyboard navigation utility
 * Reduces delays and improves responsiveness
 */

interface NavigationConfig {
  scrollDelay: number;
  smoothScroll: boolean;
  debounceMs: number;
}

const defaultConfig: NavigationConfig = {
  scrollDelay: 0, // Remove artificial delays
  smoothScroll: false, // Use instant scrolling for better performance
  debounceMs: 16 // ~60fps
};

class KeyboardNavigator {
  private config: NavigationConfig;
  private scrollTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastKeyTime = 0;
  private keyQueue: string[] = [];

  constructor(config: Partial<NavigationConfig> = {}) {
    this.config = { ...defaultConfig, ...config };
  }

  private debounceKeyPress(key: string): boolean {
    const now = Date.now();
    if (now - this.lastKeyTime < this.config.debounceMs) {
      return false;
    }
    this.lastKeyTime = now;
    return true;
  }

  private scrollToElement(element: HTMLElement): void {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    // Use requestAnimationFrame for smooth, performant scrolling
    requestAnimationFrame(() => {
      if (this.config.smoothScroll) {
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest',
          inline: 'nearest'
        });
      } else {
        // Instant scroll for better performance
        element.scrollIntoView({ 
          behavior: 'auto', 
          block: 'nearest',
          inline: 'nearest'
        });
      }
    });
  }

  public handleArrowNavigation(
    direction: 'up' | 'down',
    currentIndex: number,
    totalItems: number,
    onIndexChange: (newIndex: number) => void,
    onItemSelect: (index: number) => void,
    getElementSelector: (index: number) => string
  ): void {
    if (!this.debounceKeyPress(direction)) return;

    const newIndex = direction === 'down' 
      ? (currentIndex < totalItems - 1 ? currentIndex + 1 : 0)
      : (currentIndex > 0 ? currentIndex - 1 : totalItems - 1);

    // Update state immediately for instant UI response
    onIndexChange(newIndex);
    onItemSelect(newIndex);

    // Scroll to element with minimal delay
    if (this.config.scrollDelay > 0) {
      this.scrollTimeout = setTimeout(() => {
        const element = document.querySelector(getElementSelector(newIndex)) as HTMLElement;
        if (element) {
          this.scrollToElement(element);
        }
      }, this.config.scrollDelay);
    } else {
      // Immediate scroll
      requestAnimationFrame(() => {
        const element = document.querySelector(getElementSelector(newIndex)) as HTMLElement;
        if (element) {
          this.scrollToElement(element);
        }
      });
    }
  }

  public handleEscapeKey(onEscape: () => void): void {
    if (!this.debounceKeyPress('escape')) return;
    onEscape();
  }

  public handleEnterKey(onEnter: () => void): void {
    if (!this.debounceKeyPress('enter')) return;
    onEnter();
  }

  public cleanup(): void {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = null;
    }
  }

  public updateConfig(newConfig: Partial<NavigationConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
}

// Create singleton instance
export const keyboardNavigator = new KeyboardNavigator();

// React hook for keyboard navigation
export const useKeyboardNavigation = (
  currentIndex: number,
  totalItems: number,
  onIndexChange: (newIndex: number) => void,
  onItemSelect: (index: number) => void,
  getElementSelector: (index: number) => string,
  options: {
    onEscape?: () => void;
    onEnter?: () => void;
    enabled?: boolean;
  } = {}
) => {
  const { onEscape, onEnter, enabled = true } = options;

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          keyboardNavigator.handleArrowNavigation(
            'down',
            currentIndex,
            totalItems,
            onIndexChange,
            onItemSelect,
            getElementSelector
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          keyboardNavigator.handleArrowNavigation(
            'up',
            currentIndex,
            totalItems,
            onIndexChange,
            onItemSelect,
            getElementSelector
          );
          break;
        case 'Escape':
          if (onEscape) {
            event.preventDefault();
            keyboardNavigator.handleEscapeKey(onEscape);
          }
          break;
        case 'Enter':
          if (onEnter) {
            event.preventDefault();
            keyboardNavigator.handleEnterKey(onEnter);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      keyboardNavigator.cleanup();
    };
  }, [currentIndex, totalItems, onIndexChange, onItemSelect, getElementSelector, onEscape, onEnter, enabled]);
};

export default keyboardNavigator;
